import { useMemo } from 'react';
import { useAppSelector } from '../store/hooks';

/**
 * Maps each component key to the exact `name` value in the `modules` DB table.
 * These must match the DB exactly (case-sensitive).
 */
const MODULE_NAMES = {
  airliners:       'airliners',
  jetsetters:      'jet_setter',       // DB name is jet_setter, not jetsetters
  saxUsers:        'sub_admin',
  contracts:       'contract_management',
  cms:             'cms',
  flights:         'flight_management',
  dataAnalysis:    'data_analysis',
  accountSettings: 'account_settings',
  messages:        'notifications',
  blogs:           'blogs',
  userManagement:  'user_management',
  vendors:         'venders',          // DB name is venders (legacy spelling)
};

/**
 * Permission action names as stored in the `permissions.name` column.
 */
const ACTIONS = ['index', 'export', 'add', 'edit', 'view', 'archive', 'delete'];

/**
 * useAdminPermissions
 *
 * Returns a `perms` object keyed by module key (e.g. 'jetsetters', 'airliners').
 * Each value is an object with boolean flags for each action:
 *   { index, export, add, edit, view, archive, delete }
 *
 * For super-admins (is_admin=true) all flags are true.
 * While permissions are still loading (myPermissions is null) all flags are true
 * to avoid UI flicker.
 *
 * Usage:
 *   const { perms } = useAdminPermissions();
 *   if (perms.jetsetters.add) { ... show Add button ... }
 */
export function useAdminPermissions() {
  const { myPermissions, modulesPermissions } = useAppSelector((state) => state.admin);

  const perms = useMemo(() => {
    // Default: all true (loading state or super-admin)
    const allTrue = () =>
      ACTIONS.reduce((acc, a) => ({ ...acc, [a]: true }), {});

    const defaultPerms = Object.keys(MODULE_NAMES).reduce(
      (acc, key) => ({ ...acc, [key]: allTrue() }),
      {}
    );

    // Still loading — show everything to avoid flicker
    if (!myPermissions || !modulesPermissions || modulesPermissions.length === 0) {
      return defaultPerms;
    }

    // Super-admin — show everything
    if (myPermissions.is_admin) {
      return defaultPerms;
    }

    const myIdSet = new Set(myPermissions.permission_ids.map(Number));

    // Build a lookup: module_db_name → { action_name → permission_id }
    const moduleActionMap = {};
    modulesPermissions.forEach((mod) => {
      const dbName = (mod.name || '').toLowerCase();
      moduleActionMap[dbName] = {};
      (mod.permissions || []).forEach((p) => {
        moduleActionMap[dbName][p.name.toLowerCase()] = Number(p.id);
      });
    });

    // For each module key, compute per-action booleans
    const result = {};
    Object.entries(MODULE_NAMES).forEach(([key, dbName]) => {
      const actionMap = moduleActionMap[dbName] || {};

      // Some modules (user_management, data_analysis, notifications) only have a
      // 'check_all' permission in the DB instead of individual action permissions.
      // Treat check_all as the gate for 'index' access on those modules.
      const hasCheckAll = actionMap['check_all'] !== undefined;

      result[key] = ACTIONS.reduce((acc, action) => {
        let permId = actionMap[action];

        // If this module has no 'index' permission but has 'check_all',
        // use check_all as the index gate.
        if (action === 'index' && permId === undefined && hasCheckAll) {
          permId = actionMap['check_all'];
        }

        // If the permission doesn't exist in DB for this module, default to true
        acc[action] = permId === undefined ? true : myIdSet.has(permId);
        return acc;
      }, {});
    });

    return result;
  }, [myPermissions, modulesPermissions]);

  /**
   * Convenience: returns true if the user has ANY permission for the given module key.
   * Used to decide sidebar visibility.
   */
  const canAccessModule = (key) => {
    if (!perms[key]) return true;
    return Object.values(perms[key]).some(Boolean);
  };

  return { perms, canAccessModule };
}
